import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";

import { IconButton } from "@covertree/ui/buttons/IconButton";
import { StatusLabel } from "@covertree/ui/content/StatusLabel";
import { LoadingOrValue } from "@covertree/ui/content/LoadingOrValue";
import { formatCurrency } from "@covertree/utils";

import { formatProductTypeName } from "@utils/formatPolicyType";
import { mapPolicyStatus } from "@utils/mapPolicyStatus";

import { mapPolicyStatusLabel } from "./utils/mapPolicyStatusLabel";
import { ContentTabs } from "./ContentTabs";
import { usePolicyDetails } from "../PolicyDetailsContext";

interface Props {
  onClose(): void;
}

export function PolicyDetailsContent({ onClose }: Props) {
  const { policy, loading, error } = usePolicyDetails();
  return (
    <>
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: theme.spacing(2),
          right: theme.spacing(2),
        })}>
        <IconButton onClick={onClose} disabled={!policy && loading}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box py={2} px={3}>
        <Box mb={2}>
          <Grid container spacing={2}>
            <Grid item>
              <StatusLabel
                status={mapPolicyStatusLabel(policy?.status)}
                statusText={mapPolicyStatus(policy?.status)}
                loading={!policy && loading}
                error={error}
              />
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom align="right">
                <LoadingOrValue
                  value={formatProductTypeName(policy?.productTypeName)}
                  loading={!policy && loading}
                  error={error}
                  height={20}
                  width={120}
                />
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                &bull;
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1" gutterBottom>
                <LoadingOrValue
                  value={policy?.units[0]?.selectedPlan}
                  loading={!policy && loading}
                  error={error}
                  height={20}
                  width={50}
                />
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Typography variant="h3">
              <LoadingOrValue
                value={policy?.policyLocator}
                prefix="Policy #"
                loading={!policy && loading}
                error={error}
                height={40}
                width="100%"
              />
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h3" color="secondary">
              <LoadingOrValue
                value={formatCurrency(policy?.units[0]?.pricing?.grossPremium)}
                loading={!policy && loading}
                error={error}
                height={40}
                width="100%"
              />
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <DialogContent>
        <ContentTabs policy={policy} loading={loading} error={!!error} />
      </DialogContent>
    </>
  );
}
